/** Cliente HTTP do painel: access token só em memória, refresh por cookie httpOnly + CSRF. */
export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly corpo: { message?: string | string[]; codigo?: string; [k: string]: unknown },
  ) {
    super(
      Array.isArray(corpo.message) ? corpo.message.join(', ') : (corpo.message ?? `Erro ${status}`),
    );
  }
  /** Código estável do erro (`{ error, code }` da API), para decidir o que mostrar. */
  get code(): string | undefined {
    const c = this.corpo.code ?? this.corpo.codigo;
    return typeof c === 'string' ? c : undefined;
  }
}

let accessToken: string | null = null;
/** Último erro visto, enviado junto quando a pessoa abre um chamado de suporte. */
let ultimoErro: string | null = null;
export const lerUltimoErro = () => ultimoErro;
let filial = 'todas';
let renovando: Promise<boolean> | null = null;
let aoExpirar: (() => void) | null = null;
let aoBloquear: (() => void) | null = null;

export const definirToken = (t: string | null) => (accessToken = t);
export const tokenAtual = () => accessToken;
export const definirFilial = (f: string) => (filial = f);
export const filialAtual = () => filial;
export const quandoSessaoExpirar = (fn: () => void) => (aoExpirar = fn);
/** Assinatura bloqueada por atraso (402): o painel mostra a tela de pagamento. */
export const quandoAssinaturaBloquear = (fn: (() => void) | null) => (aoBloquear = fn);

function lerCookie(nome: string): string {
  return (
    document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${nome}=`))
      ?.split('=')[1] ?? ''
  );
}

/** Renova o access token (uma chamada por vez). */
export function renovarSessao(): Promise<boolean> {
  renovando ??= fetch(`${API_URL}/auth/renovar`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': lerCookie('erp_csrf') },
  })
    .then(async (r) => {
      if (!r.ok) return false;
      const corpo = (await r.json()) as { accessToken: string };
      accessToken = corpo.accessToken;
      return true;
    })
    .catch(() => false)
    .finally(() => (renovando = null));
  return renovando;
}

/** Hash curto (FNV-1a) da ação: mesma ação + mesmo corpo = mesma chave. */
export function chaveIdempotencia(texto: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < texto.length; i++) {
    const c = texto.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x811c9dc5) >>> 0;
  }
  return `p${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

export async function api<T>(
  caminho: string,
  init: RequestInit & { json?: unknown } = {},
  tentativa = 0,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('X-Filial', filial);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  const metodo = (init.method ?? 'GET').toUpperCase();
  // Trava de clique duplo: a mesma ação com o mesmo conteúdo só é aceita uma vez em poucos segundos
  if (metodo !== 'GET' && (body === undefined || typeof body === 'string'))
    headers.set('Idempotency-Key', chaveIdempotencia(`${metodo} ${caminho} ${body ?? ''}`));
  let r: Response;
  try {
    r = await fetch(`${API_URL}${caminho}`, { ...init, body, headers, credentials: 'include' });
  } catch {
    // Sinal caiu ou servidor fora do ar: nada do que foi digitado se perde (o formulário continua)
    const semRede = typeof navigator !== 'undefined' && navigator.onLine === false;
    const e = new ApiError(0, {
      codigo: 'sem_conexao',
      message: semRede
        ? 'Sem internet agora. O que você digitou continua na tela: tente de novo quando o sinal voltar.'
        : 'Não foi possível falar com o sistema. Confira a internet e tente de novo; o que você digitou continua na tela.',
    });
    ultimoErro = `rede ${caminho}`;
    throw e;
  }
  if (r.status === 401 && tentativa === 0 && !caminho.startsWith('/auth/')) {
    if (await renovarSessao()) return api<T>(caminho, init, 1);
    aoExpirar?.();
  }
  if (r.status === 204) return undefined as T;
  const corpo = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) {
    // Serviço fora do ar ou erro inesperado: mensagem clara, nunca código técnico
    if (r.status >= 502 && r.status <= 504 && !corpo.message)
      corpo.message = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
    else if (r.status >= 500 && !corpo.message)
      corpo.message =
        'Algo deu errado do nosso lado. Já fomos avisados; tente de novo em instantes.';
    const e = new ApiError(r.status, corpo);
    if (r.status === 402 && corpo.codigo === 'assinatura_bloqueada') aoBloquear?.();
    ultimoErro = `${r.status} ${caminho}: ${e.message}`.slice(0, 500);
    throw e;
  }
  return corpo as T;
}

export async function sair(): Promise<void> {
  await fetch(`${API_URL}/auth/sair`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': lerCookie('erp_csrf') },
  }).catch(() => undefined);
  accessToken = null;
}

/** Baixa um arquivo autenticado (o token vai no cabeçalho, nunca na URL). */
export async function baixarArquivo(caminho: string, nome: string): Promise<void> {
  const exec = () =>
    fetch(`${API_URL}${caminho}`, {
      credentials: 'include',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'X-Filial': filial,
      },
    });
  let r = await exec();
  if (r.status === 401 && (await renovarSessao())) r = await exec();
  if (!r.ok) {
    // Mostra o motivo da API (ex.: período inválido), não só "não foi possível"
    const corpo = (await r.json().catch(() => null)) as
      ConstructorParameters<typeof ApiError>[1] | null;
    throw new ApiError(
      r.status,
      corpo?.message ? corpo : { message: 'Não foi possível baixar o arquivo' },
    );
  }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Mensagem pronta para a tela: a da API quando ela explicou, senão um texto amigável. */
export const mensagemErro = (e: unknown, padrao = 'Não foi possível concluir. Tente de novo.') =>
  e instanceof ApiError ? e.message : padrao;
